interface Reporter {
  error(value: unknown): unknown
  flush(): Promise<void>
}
type ProcessEvents = 'uncaughtException' | 'unhandledRejection'
interface ProcessHost {
  versions?: { node?: string }
  on(event: ProcessEvents, listener: (error: unknown) => void): unknown
  removeListener(event: ProcessEvents, listener: (error: unknown) => void): unknown
  exit(code: number): void
}
interface ErrorHost {
  process?: ProcessHost
  addEventListener?: typeof globalThis.addEventListener
  removeEventListener?: typeof globalThis.removeEventListener
  [registryKey]?: Registration
}
interface Registration {
  reporter: Reporter
  detach(): void
}
const registryKey = Symbol.for('@w/tel/global-errors')
const FLUSH_TIMEOUT_MS = 2000

export function bindGlobalErrors(reporter: Reporter, host: ErrorHost = globalThis): () => void {
  let registration = host[registryKey]
  if (registration) {
    registration.reporter = reporter
  } else {
    registration = { reporter, detach: () => {} }
    const current = registration
    const node = host.process
    if (node?.versions?.node && typeof node.on === 'function' && typeof node.removeListener === 'function' && typeof node.exit === 'function') {
      let terminating = false
      const fatal = (error: unknown) => {
        if (terminating) return
        terminating = true
        flushAndExit(current.reporter, error, node)
      }
      node.on('uncaughtException', fatal)
      node.on('unhandledRejection', fatal)
      current.detach = () => {
        node.removeListener('uncaughtException', fatal)
        node.removeListener('unhandledRejection', fatal)
      }
    } else if (typeof host.addEventListener === 'function' && typeof host.removeEventListener === 'function') {
      const report = (error: unknown) => {
        try {
          current.reporter.error(error === undefined ? new Error('Unhandled rejection with undefined reason') : error)
        } catch (reportError) {
          console.error('Global telemetry reporting failed', error, reportError)
        }
      }
      const onError = (event: ErrorEvent) => {
        report(event.error ?? new Error(event.message))
      }
      const onRejection = (event: PromiseRejectionEvent) => {
        report(event.reason)
      }
      host.addEventListener('error', onError)
      host.addEventListener('unhandledrejection', onRejection)
      current.detach = () => {
        host.removeEventListener?.('error', onError)
        host.removeEventListener?.('unhandledrejection', onRejection)
      }
    }
    host[registryKey] = current
  }
  const current = registration
  return () => {
    if (host[registryKey] !== current || current.reporter !== reporter) return
    current.detach()
    delete host[registryKey]
  }
}

function flushAndExit(owner: Reporter, error: unknown, node: ProcessHost): void {
  const timeout = setTimeout(() => { node.exit(1) }, FLUSH_TIMEOUT_MS)
  const finish = () => {
    clearTimeout(timeout)
    node.exit(1)
  }
  const flush = async () => {
    try {
      owner.error(error === undefined ? new Error('Unhandled rejection with undefined reason') : error)
    } catch (reportError) {
      console.error('Fatal telemetry reporting failed', error, reportError)
    }
    await owner.flush()
  }
  void flush().then(finish, (flushError: unknown) => {
    // Telemetry itself failed; stderr is the last reporting boundary.
    console.error('Fatal telemetry flush failed', error, flushError)
    finish()
  })
}
